// ✅ src/pages/VehicleV2/BillAddSpares.jsx  (UPDATED FULL FILE)
// Supports:
// - selecting rows
// - passing BOTH storesIssueId + storesIssueDetId
// - saving selection in localStorage for BillGeneration tab / page
//
// Pattern updated to match your BillAddVehicle style:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + gridLoading + billingLoading states
// - cleaner init / search / bill flow
// - consistent card / section / button style

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSearch,
  FaFileInvoiceDollar,
  FaSyncAlt,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/billAddSpares";

/* ---------------- helpers ---------------- */
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const clean = (v) => String(v ?? "").trim();

const isDDMMYYYY = (s) =>
  typeof s === "string" && /^(\d{2})\/(\d{2})\/(\d{4})$/.test(s);

const parseDDMMYYYY = (s) => {
  if (!isDDMMYYYY(s)) return null;
  const [dd, mm, yyyy] = s.split("/").map((x) => Number(x));
  const dt = new Date(yyyy, mm - 1, dd);
  if (
    dt.getFullYear() !== yyyy ||
    dt.getMonth() !== mm - 1 ||
    dt.getDate() !== dd
  ) {
    return null;
  }
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const isFutureDate = (s) => {
  const dt = parseDDMMYYYY(s);
  if (!dt) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return dt.getTime() > now.getTime();
};

const numOr0 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const defaultFilters = {
  fromDate: "",
  toDate: "",
  materialCode: "",
  materialName: "",
};

const mapRow = (r) => ({
  storesIssueDetId: toInt(
    r?.fld_StoresIssuedetid ??
      r?.fld_storesIssuedetid ??
      r?.storesIssueDetId ??
      r?.detId ??
      r?.detailId ??
      0,
    0
  ),
  storesIssueId: toInt(
    r?.fld_StoresIssueid ??
      r?.fld_storesIssueid ??
      r?.storesIssueId ??
      r?.issueId ??
      0,
    0
  ),

  materialCode: toStr(r?.fld_itemcode ?? r?.itemCode ?? r?.materialCode),
  materialName: toStr(r?.fld_itemname ?? r?.itemName ?? r?.materialName),
  uom: toStr(r?.fld_uomname ?? r?.uomName ?? r?.uom),
  qty: toStr(r?.fld_fifoqty ?? r?.fifoQty ?? r?.qty),
  price: toStr(r?.fld_fifoprice ?? r?.fifoPrice ?? r?.price),
  amount: toStr(r?.fld_itemamnt ?? r?.itemAmount ?? r?.amount),
  cumulativeAmount: toStr(
    r?.fld_cumamnt ?? r?.cumAmount ?? r?.cumulativeAmount
  ),
  date: toStr(r?.fld_date ?? r?.date),
});

const AutoList = ({ show, items, onPick, isDark }) => {
  if (!show) return null;

  return (
    <div
      className={`absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-lg border shadow ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      {items.length === 0 ? (
        <div
          className={`px-3 py-2 text-sm ${
            isDark ? "text-gray-300" : "text-gray-600"
          }`}
        >
          No matches
        </div>
      ) : (
        items.map((it, idx) => (
          <button
            key={`${it.value}-${idx}`}
            type="button"
            onClick={() => onPick(it)}
            className={`w-full text-left px-3 py-2 text-sm hover:opacity-90 ${
              isDark
                ? "text-gray-100 hover:bg-gray-700"
                : "text-gray-900 hover:bg-gray-100"
            }`}
          >
            {it.label}
          </button>
        ))
      )}
    </div>
  );
};

const BillAddSpares = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ---------------- shared local/session pattern ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const vehId =
    toInt(params.get("VehID")) ||
    toInt(params.get("Vehid")) ||
    toInt(params.get("vehId")) ||
    toInt(params.get("vehicleId")) ||
    0;

  const vehType = (
    params.get("Type") ||
    params.get("type") ||
    params.get("brtype") ||
    "O"
  ).toUpperCase();

  const STORAGE_KEY = useMemo(() => `billGen_selection_${vehId}`, [vehId]);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  /* ---------------- state ---------------- */
  const [pageLoading, setPageLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);

  const [header, setHeader] = useState({
    vehicleNumber: "",
    period: "",
    toDate: "",
    basis: "",
    vehicleType: "",
  });

  const [filters, setFilters] = useState(defaultFilters);
  const [list, setList] = useState([]);

  // selected: { ["detId|issueId"]: { storesIssueDetId, storesIssueId } }
  const [selected, setSelected] = useState({});

  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);
  const [showCodeSug, setShowCodeSug] = useState(false);
  const [showNameSug, setShowNameSug] = useState(false);

  const codeTimer = useRef(null);
  const nameTimer = useRef(null);

  const isBusy = pageLoading || gridLoading || billingLoading;

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const valueBoxClass = useMemo(
    () =>
      `px-3 py-2 rounded-lg border ${
        isDark
          ? "bg-gray-700/20 border-gray-600 text-gray-200"
          : "bg-gray-50 border-gray-200 text-gray-800"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ---------------- derived ---------------- */
  const checkedCount = useMemo(() => Object.keys(selected).length, [selected]);
  const recordsCount = useMemo(() => list.length, [list]);

  const columns = useMemo(
    () => [
      { key: "materialCode", label: "Material Code", w: "140px" },
      { key: "materialName", label: "Material Name", w: "220px" },
      { key: "uom", label: "UOM", w: "100px" },
      { key: "qty", label: "Material Qty", w: "120px" },
      { key: "price", label: "Material Price", w: "130px" },
      { key: "amount", label: "Material Amount", w: "140px" },
      {
        key: "cumulativeAmount",
        label: "Cumulative Amount",
        w: "160px",
      },
      { key: "date", label: "Date", w: "120px" },
    ],
    []
  );

  /* ---------------- helpers ---------------- */
  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const validateDates = useCallback(() => {
    const { fromDate, toDate } = filters;

    if (fromDate && !isDDMMYYYY(fromDate)) {
      return canUse(false, "From date must be dd/MM/yyyy");
    }
    if (toDate && !isDDMMYYYY(toDate)) {
      return canUse(false, "To date must be dd/MM/yyyy");
    }

    if (fromDate && parseDDMMYYYY(fromDate) == null) {
      return canUse(false, "Invalid From date");
    }
    if (toDate && parseDDMMYYYY(toDate) == null) {
      return canUse(false, "Invalid To date");
    }

    if (fromDate && isFutureDate(fromDate)) {
      return canUse(false, "Select from date lesser than current date");
    }
    if (toDate && isFutureDate(toDate)) {
      return canUse(false, "Select to date lesser than current date");
    }

    if (fromDate && toDate) {
      const f = parseDDMMYYYY(fromDate);
      const t = parseDDMMYYYY(toDate);
      if (t.getTime() < f.getTime()) {
        return canUse(false, "Please select from date less than to date");
      }
    }

    return true;
  }, [filters, canUse]);

  const getStoresIssueDetId = useCallback(
    (r) =>
      toInt(
        r?.storesIssueDetId ??
          r?.fld_StoresIssuedetid ??
          r?.fld_storesIssuedetid ??
          r?.detId ??
          r?.detailId ??
          0,
        0
      ),
    []
  );

  const getStoresIssueId = useCallback(
    (r, idx) =>
      toInt(
        r?.storesIssueId ??
          r?.fld_StoresIssueid ??
          r?.fld_storesIssueid ??
          r?.issueId ??
          r?.id ??
          idx + 1,
        0
      ),
    []
  );

  const getRowKey = useCallback(
    (r, idx) => `${getStoresIssueDetId(r)}|${getStoresIssueId(r, idx)}`,
    [getStoresIssueDetId, getStoresIssueId]
  );

  const renderCell = useCallback((row, key) => {
    const v = row?.[key];
    if (v === null || v === undefined || v === "") return "-";
    return String(v);
  }, []);

  const saveSelectionToLocalStorage = useCallback(
    (sparesIds, sparesDetIds, sparesTotalAmount) => {
      try {
        const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");

        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            ...existing,
            sparesIds: Array.isArray(sparesIds)
              ? sparesIds.map((x) => Number(x) || 0)
              : [],
            sparesDetIds: Array.isArray(sparesDetIds)
              ? sparesDetIds.map((x) => Number(x) || 0)
              : [],
            sparesTotalAmount:
              sparesTotalAmount != null
                ? String(sparesTotalAmount)
                : existing?.sparesTotalAmount ?? "0",
            fromDate: filters.fromDate,
            toDate: filters.toDate,
            updatedAt: new Date().toISOString(),
          })
        );
      } catch {
        // ignore
      }
    },
    [STORAGE_KEY, filters]
  );

  const goToBillGenerationWithData = useCallback(
    (handoff, sparesIds, sparesDetIds, sparesTotalAmount) => {
      const payload = {
        vehId,
        vehType,
        ...handoff,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      };

      sessionStorage.setItem("BillGen_SparesHandoff", JSON.stringify(payload));

      saveSelectionToLocalStorage(sparesIds, sparesDetIds, sparesTotalAmount);

      navigate(
        `/vehicle/BillGeneration?VehID=${encodeURIComponent(
          vehId
        )}&Type=${encodeURIComponent(vehType)}`,
        {
          state: payload,
        }
      );
    },
    [vehId, vehType, filters, navigate, saveSelectionToLocalStorage]
  );

  /* ---------------- autocomplete ---------------- */
  const fetchAuto = useCallback(
    async (kind, prefix) => {
      const url =
        kind === "code"
          ? `${API_BASE}/autocomplete/materialCode`
          : `${API_BASE}/autocomplete/materialName`;

      const { data } = await axios.get(url, {
        params: { prefix: prefix || "" },
        headers: commonHeaders,
      });

      if (!data?.success) return [];

      const raw = data.list || data.rows || [];
      const list =
        raw.length > 0 && typeof raw[0] === "string"
          ? raw.map((s) => ({ value: s, label: s }))
          : raw.map((x) => ({
              value: toStr(x?.value ?? x?.code ?? x?.name ?? x),
              label: toStr(x?.label ?? x?.code ?? x?.name ?? x),
            }));

      const seen = new Set();
      return list.filter((it) => {
        const k = it.value;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    },
    [commonHeaders]
  );

  const onMaterialCodeChange = useCallback(
    (value) => {
      setFilters((p) => ({ ...p, materialCode: value }));
      setShowCodeSug(true);

      if (codeTimer.current) clearTimeout(codeTimer.current);
      codeTimer.current = setTimeout(async () => {
        try {
          const rows = await fetchAuto("code", value);
          setCodeSug(rows);
        } catch {
          setCodeSug([]);
        }
      }, 250);
    },
    [fetchAuto]
  );

  const onMaterialNameChange = useCallback(
    (value) => {
      setFilters((p) => ({ ...p, materialName: value }));
      setShowNameSug(true);

      if (nameTimer.current) clearTimeout(nameTimer.current);
      nameTimer.current = setTimeout(async () => {
        try {
          const rows = await fetchAuto("name", value);
          setNameSug(rows);
        } catch {
          setNameSug([]);
        }
      }, 250);
    },
    [fetchAuto]
  );

  /* =========================================================
     API LOADERS
     ========================================================= */
  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;
    if (!canUse(vehId, "VehID missing in URL. Use ?VehID=79&Type=O")) return;

    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          vehicleId: vehId,
          branchId,
          loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Init failed");
      }

      setHeader((p) => ({
        ...p,
        vehicleNumber:
          data?.vehicleNumber || data?.vehicleNo || data?.vehicleRegNo || "",
      }));

      setFilters((p) => ({
        ...p,
        materialCode: toStr(data?.defaultMaterialCode || ""),
      }));

      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const sel = JSON.parse(raw);
          const sIds = Array.isArray(sel?.sparesIds) ? sel.sparesIds : [];
          const sDet = Array.isArray(sel?.sparesDetIds) ? sel.sparesDetIds : [];

          if (sIds.length) {
            const map = {};
            for (let i = 0; i < sIds.length; i++) {
              const detId = toInt(sDet[i], 0) || 0;
              const issueId = toInt(sIds[i], 0) || 0;
              map[`${detId}|${issueId}`] = {
                storesIssueDetId: detId,
                storesIssueId: issueId,
              };
            }
            setSelected(map);
          } else {
            setSelected({});
          }
        } else {
          setSelected({});
        }
      } catch {
        setSelected({});
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Initialization failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [loginUserId, branchId, vehId, commonHeaders, canUse, STORAGE_KEY]);

  const loadList = useCallback(async () => {
    if (!validateDates()) return;
    if (!canUse(vehId, "VehID missing in URL")) return;

    try {
      setGridLoading(true);

      const { data } = await axios.get(`${API_BASE}/list`, {
        params: {
          vehicleId: vehId,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          materialCode: filters.materialCode,
          materialName: filters.materialName,
          branchId,
          loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        setList([]);
        toast.warn(data?.message || "No data found");
        return;
      }

      const rows = (data?.rows || data?.list || []).map(mapRow);
      setList(rows);

      if (!rows.length) {
        toast.info("No Records Found!");
      } else {
        toast.success(`Found ${rows.length} record(s)`);
      }
    } catch (err) {
      console.error(err);
      setList([]);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to load list"
      );
    } finally {
      setGridLoading(false);
    }
  }, [
    validateDates,
    canUse,
    vehId,
    filters,
    branchId,
    loginUserId,
    commonHeaders,
  ]);

  /* ---------------- handlers ---------------- */
  const onFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  }, []);

  const toggleRow = useCallback(
    (row, idx) => {
      const storesIssueDetId = getStoresIssueDetId(row) || 0;
      const storesIssueId = getStoresIssueId(row, idx) || 0;
      const key = `${storesIssueDetId}|${storesIssueId}`;

      setSelected((p) => {
        const next = { ...p };
        if (next[key] !== undefined) delete next[key];
        else {
          next[key] = {
            storesIssueDetId,
            storesIssueId,
          };
        }
        return next;
      });
    },
    [getStoresIssueDetId, getStoresIssueId]
  );

  const handleRefresh = useCallback(async () => {
    setList([]);
    setCodeSug([]);
    setNameSug([]);
    await loadInit();
  }, [loadInit]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onBill = useCallback(async () => {
    const selectedRows = Object.values(selected || {});
    const sparesIds = selectedRows
      .map((x) => toInt(x?.storesIssueId, 0))
      .filter((x) => x > 0);

    if (!canUse(sparesIds.length > 0, "Select at least one spare")) return;

    const sparesDetIds = selectedRows.map(
      (x) => toInt(x?.storesIssueDetId, 0) || 0
    );

    const selectedPayload = selectedRows.map(({ storesIssueDetId, storesIssueId }) => {
      const row = list.find(
        (r) =>
          toInt(r?.storesIssueDetId, 0) === toInt(storesIssueDetId, 0) &&
          toInt(r?.storesIssueId, 0) === toInt(storesIssueId, 0)
      );

      return {
        storesIssueDetId: toInt(storesIssueDetId, 0) || 0,
        storesIssueId: toInt(storesIssueId, 0) || 0,
        itemAmount: numOr0(row?.amount ?? row?.itemAmount ?? row?.fld_itemamnt),
      };
    });

    try {
      setBillingLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/add`,
        {
          loginUserId,
          vehicleId: vehId,
          type: vehType,
          selected: selectedPayload,
        },
        {
          headers: commonHeaders,
        }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Add failed");
      }

      toast.success(data?.message || "Spares selected");

      const handoff = {
        vehicleId: vehId,
        type: vehType,
        BVehSparesID: data?.BVehSparesID,
        BVehSparesDetID: data?.BVehSparesDetID,
        BVehSparesTotal: data?.BVehSparesTotal,
      };

      goToBillGenerationWithData(
        handoff,
        sparesIds,
        sparesDetIds,
        data?.BVehSparesTotal ?? 0
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Bill error");
    } finally {
      setBillingLoading(false);
    }
  }, [
    selected,
    list,
    canUse,
    loginUserId,
    vehId,
    vehType,
    commonHeaders,
    goToBillGenerationWithData,
  ]);

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    const onDoc = (e) => {
      const t = e.target;
      if (!t.closest?.("[data-autobox='code']")) setShowCodeSug(false);
      if (!t.closest?.("[data-autobox='name']")) setShowNameSug(false);
    };

    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  useEffect(() => {
    return () => {
      if (codeTimer.current) clearTimeout(codeTimer.current);
      if (nameTimer.current) clearTimeout(nameTimer.current);
    };
  }, []);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              BILL GENERATION - ADD SPARES
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Bill Generation - Add Spares
              </div>

              <div
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Branch ID: {branchId || "-"} {" • "} User ID:{" "}
                {loginUserId || "-"} {" • "} Vehicle ID: {vehId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>Selected: {checkedCount}</span>
                <span className={pillClass}>Records: {recordsCount}</span>
                <span className={pillClass}>
                  Vehicle Mode: {vehType === "O" ? "Own" : "Hire"}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                className={`${buttonClass} ${hoverClass} text-white px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isBusy ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  <FaArrowLeft />
                  Back
                </span>
              </button>
            </div>
          </div>

          {/* Filters + Header Details */}
          <div className={sectionClass}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>From Date</label>
                <input
                  name="fromDate"
                  value={filters.fromDate}
                  onChange={onFilterChange}
                  className={inputClass}
                  placeholder="dd/MM/yyyy"
                  disabled={isBusy}
                />
              </div>

              <div>
                <label className={labelClass}>To Date</label>
                <input
                  name="toDate"
                  value={filters.toDate}
                  onChange={onFilterChange}
                  className={inputClass}
                  placeholder="dd/MM/yyyy"
                  disabled={isBusy}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div className="relative" data-autobox="code">
                <label className={labelClass}>Material Code</label>
                <input
                  value={filters.materialCode}
                  onChange={(e) => onMaterialCodeChange(e.target.value)}
                  className={inputClass}
                  placeholder="Material Code"
                  disabled={isBusy}
                  onFocus={() => setShowCodeSug(true)}
                />
                <AutoList
                  show={showCodeSug && clean(filters.materialCode).length > 0}
                  items={codeSug}
                  isDark={isDark}
                  onPick={(it) => {
                    setFilters((p) => ({ ...p, materialCode: it.value }));
                    setShowCodeSug(false);
                  }}
                />
              </div>

              <div className="relative" data-autobox="name">
                <label className={labelClass}>Material Name</label>
                <input
                  value={filters.materialName}
                  onChange={(e) => onMaterialNameChange(e.target.value)}
                  className={inputClass}
                  placeholder="Material Name"
                  disabled={isBusy}
                  onFocus={() => setShowNameSug(true)}
                />
                <AutoList
                  show={showNameSug && clean(filters.materialName).length > 0}
                  items={nameSug}
                  isDark={isDark}
                  onPick={(it) => {
                    setFilters((p) => ({ ...p, materialName: it.value }));
                    setShowNameSug(false);
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <div>
                <label className={labelClass}>Vehicle Number</label>
                <div className={valueBoxClass}>
                  {header.vehicleNumber || "-"}
                </div>
              </div>

              <div>
                <label className={labelClass}>Period</label>
                <div className={valueBoxClass}>{header.period || "-"}</div>
              </div>

              <div>
                <label className={labelClass}>To Date</label>
                <div className={valueBoxClass}>{header.toDate || "-"}</div>
              </div>
            </div>

            <div className="flex justify-end mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={loadList}
                disabled={isBusy}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60`}
              >
                <FaSearch />
                {gridLoading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>

          {/* GRID */}
          <div className="mt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
              <div
                className={`text-lg font-semibold ${
                  isDark ? "text-white" : "text-gray-800"
                }`}
              >
                Spares List
              </div>

              <div
                className={`text-sm ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Vehicle Number:{" "}
                <span className="font-semibold">{header.vehicleNumber || "-"}</span>
                {" • "}
                Records: <span className="font-semibold">{list.length}</span>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-[1300px] w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-center">Check</th>
                    <th className="px-4 py-3 text-center">S.No</th>
                    {columns.map((c) => (
                      <th
                        key={c.key}
                        className="px-4 py-3 text-center whitespace-nowrap"
                        style={{ width: c.w }}
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody
                  className={
                    isDark
                      ? "bg-gray-900 text-gray-100"
                      : "bg-white text-gray-800"
                  }
                >
                  {list.length === 0 ? (
                    <tr>
                      <td
                        colSpan={2 + columns.length}
                        className="px-4 py-10 text-center text-gray-500"
                      >
                        {gridLoading || pageLoading
                          ? "Loading..."
                          : "No records found"}
                      </td>
                    </tr>
                  ) : (
                    list.map((row, idx) => {
                      const key = getRowKey(row, idx);
                      const checked = selected[key] !== undefined;
                      const detId = getStoresIssueDetId(row);
                      const issueId = getStoresIssueId(row, idx);

                      return (
                        <tr
                          key={`${key}-${idx}`}
                          title={`sparesDetId=${toInt(
                            detId,
                            0
                          )} | sparesId=${toInt(issueId, 0)}`}
                          className={
                            isDark
                              ? "border-t border-gray-800 hover:bg-gray-800/60"
                              : "border-t border-gray-100 hover:bg-gray-50"
                          }
                        >
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRow(row, idx)}
                              disabled={isBusy}
                            />
                          </td>

                          <td className="px-4 py-3 text-center">{idx + 1}</td>

                          {columns.map((c) => (
                            <td
                              key={c.key}
                              className="px-4 py-3 text-center whitespace-nowrap"
                            >
                              {renderCell(row, c.key)}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>

                <tfoot>
                  <tr className={isDark ? "bg-gray-800" : "bg-gray-50"}>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={onBill}
                        disabled={isBusy || checkedCount === 0}
                        className={`${buttonClass} ${hoverClass} text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 shadow disabled:opacity-60`}
                      >
                        <FaFileInvoiceDollar />
                        {billingLoading ? "Processing..." : "Add"}
                      </button>
                    </td>
                    <td colSpan={1 + columns.length}></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {isBusy && (
              <div
                className={`mt-3 text-sm ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                {pageLoading
                  ? "Loading page..."
                  : gridLoading
                  ? "Loading grid..."
                  : billingLoading
                  ? "Preparing bill..."
                  : "Loading..."}
              </div>
            )}
          </div>

          <div
            className={`mt-4 text-xs ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}
          >
            Tip: open like <b>/vehicle/BillAddSpares?VehID=79&Type=O</b>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BillAddSpares;